import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import SidePanel from './SidePanel';
import AnimatedHamburger from './AnimatedHamburger';
import StoreLocator from './StoreLocator';
import UserManagement from './UserManagement';
import AdminOrderManagement from './AdminOrderManagment';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import Messages from './Messages';
import logo from '../assets/wycoughlogo.png'
import { format } from 'date-fns';
import CropImageModal from './CropImageModal';
import CategoryCombobox from './CategoryComboBox';
import AnnouncementForm from './AnnouncementForm';
import ManageAnnouncements from './ManageAnnouncements';

const AdminDashboard = ({ onLogout }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [newProduct, setNewProduct] = useState({ 
    name: '', 
    description: '', 
    price: '', 
    category: '',
    subcategory: '',
    details: '',
    imageUrls: [], 
    hasBulkPricing: false,
  bulkOptions: [
    { quantity: 2, price: '' },
    { quantity: 3, price: '' },
    { quantity: 4, price: '' },
    { quantity: 5, price: '' },
    { quantity: 6, price: '' },
    { quantity: 7, price: '' },
    { quantity: 8, price: '' }
  ]
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [activeView, setActiveView] = useState('products');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageFiles, setImageFiles] = useState([]); // Changed to handle multiple files
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '', type: '' });
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [filterSortOption, setFilterSortOption] = useState("sort:asc");
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]); // Changed to handle multiple images
  const [croppingImage, setCroppingImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [currentCropIndex, setCurrentCropIndex] = useState(0); // Track which image is being cropped
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [existingSubcategories, setExistingSubcategories] = useState([]);
  const [existingCategories, setExistingCategories] = useState([]);
  const [isDeal, setIsDeal] = useState(editingProduct?.category === 'Deals');
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);



  useEffect(() => {
  if (editingProduct?.category === 'Deals') {
    setIsDeal(true);
  } else {
    setIsDeal(false);
  }
}, [editingProduct?.category]);

  const db = getFirestore();
  const storage = getStorage();

  const fetchProducts = useCallback(async () => {
  const productsCollection = collection(db, 'Products');
  const productSnapshot = await getDocs(productsCollection);
  const productList = productSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  setProducts(productList);

  // Unique categories
  const uniqueCategories = ['All', ...new Set(productList.map(product => product.category?.trim() || 'Uncategorized'))];
  setCategories(uniqueCategories);
  setExistingCategories(uniqueCategories.filter(c => c !== 'All')); // <-- This is needed for the edit dropdown

  // Unique subcategories
  const uniqueSubcategories = [...new Set(
    productList
      .map(product => product.subcategory?.trim())
      .filter(Boolean)
  )];
  setExistingSubcategories(uniqueSubcategories);
}, [db]);


  function formatDate(date) {
    if (!date) return 'N/A';
    
    // If it's a Firestore timestamp (has a toDate function), convert it
    const dateObj = date.toDate ? date.toDate() : new Date(date);
  
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return dateObj.toLocaleDateString(undefined, options);
  }
  
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Append to existing files instead of replacing
      const updatedImageFiles = [...imageFiles, ...files];
      setImageFiles(updatedImageFiles);
      
      // Create preview URLs for the new files
      const imagePreviewPromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });
  
      Promise.all(imagePreviewPromises).then(newPreviews => {
        if (editingProduct) {
          // Append to existing preview images
          const updatedPreviews = [...(editingProduct.imagePreviews || []), ...newPreviews];
          setEditingProduct({ ...editingProduct, imagePreviews: updatedPreviews });
          
          // Show cropper for the first new image
          setCroppingImage(newPreviews[0]);
          setCurrentCropIndex(editingProduct.imagePreviews?.length || 0);
          setShowCropper(true);
        } else {
          // Append to existing selected images
          const updatedSelectedImages = [...selectedImages, ...newPreviews];
          setSelectedImages(updatedSelectedImages);
          
          // Show cropper for the first new image
          setCroppingImage(newPreviews[0]);
          setCurrentCropIndex(selectedImages.length);
          setShowCropper(true);
        }
      });
    }
  };


  // Handle drag start
const handleDragStart = (e, index) => {
  setDraggedImageIndex(index);
};

// Handle drag over
const handleDragOver = (e) => {
  e.preventDefault(); // Prevent default to allow drop
};

// Handle drop
const handleDrop = (e, dropIndex) => {
  e.preventDefault();
  if (draggedImageIndex === null) return;

  // Create a new array of images with updated order
  const updatedImages = [...editingProduct.imageUrls];
  const [draggedImage] = updatedImages.splice(draggedImageIndex, 1);
  updatedImages.splice(dropIndex, 0, draggedImage);

  // Update the state with the new order
  setEditingProduct({
    ...editingProduct,
    imageUrls: updatedImages
  });

  // Reset the dragged index
  setDraggedImageIndex(null);
};


  const safeFormatPrice = (price) => {
  if (price === null || price === undefined || price === '') {
    return '0.00';
  }
  
  try {
    return typeof price === 'number'
      ? price.toFixed(2)
      : parseFloat(price).toFixed(2);
  } catch (error) {
    console.error('Error formatting price:', price, error);
    return '0.00';
  }
};
  
  
  const handleCropComplete = (croppedBlob) => {
    const file = new File([croppedBlob], `cropped-image-${currentCropIndex}.jpg`, { type: 'image/jpeg' });
    
    // Replace the file in the imageFiles array
    const newImageFiles = [...imageFiles];
    newImageFiles[currentCropIndex] = file;
    setImageFiles(newImageFiles);
    
    // Create a preview for the cropped image
    const reader = new FileReader();
    reader.onloadend = () => {
      // Update the appropriate preview collection depending on context
      if (editingProduct) {
        // When editing a product
        const newPreviews = [...(editingProduct.imagePreviews || [])];
        newPreviews[currentCropIndex] = reader.result;
        setEditingProduct({ ...editingProduct, imagePreviews: newPreviews });
      } else {
        // When adding a new product
        const newPreviews = [...selectedImages];
        newPreviews[currentCropIndex] = reader.result;
        setSelectedImages(newPreviews);
      }
      
      // Move to next image if available
      if (currentCropIndex < imageFiles.length - 1) {
        setCurrentCropIndex(currentCropIndex + 1);
        // Select the correct source for the next image to crop
        const nextImageSrc = editingProduct 
          ? (editingProduct.imagePreviews || [])[currentCropIndex + 1]
          : selectedImages[currentCropIndex + 1];
        
        setCroppingImage(nextImageSrc);
      } else {
        // Done cropping all images
        setShowCropper(false);
        setCroppingImage(null);
        setCurrentCropIndex(0); // Reset for next upload session
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadImages = async () => {
    if (!imageFiles || imageFiles.length === 0) return [];
    
    console.log("Uploading images:", imageFiles.length); // Debug log
    
    const uploadPromises = imageFiles.map(async (file, index) => {
      if (!file) {
        console.warn(`Missing file at index ${index}`);
        return null;
      }
      
      const imageRef = ref(storage, `product-images/${uuidv4()}`);
      await uploadBytes(imageRef, file);
      return getDownloadURL(imageRef);
    });
    
    // Filter out any null results from failed uploads
    const urls = await Promise.all(uploadPromises);
    return urls.filter(url => url !== null);
  };

  const addProduct = async (e) => {
    e.preventDefault();
  
    // Validation: Check if required fields are filled
   if (!newProduct.name  || (!newProduct.isDeal && !newProduct.price) || 
      (newProduct.isDeal && (!newProduct.dealQuantity || !newProduct.dealPrice))) {
    setAlertInfo({
      show: true,
      message: 'Please fill in all required fields.',
      type: 'error',
    });
  
      setTimeout(() => {
        setAlertInfo({ show: false, message: '', type: '' });
      }, 5000);
  
      return;
    }
    
    try {
      // Check if we have processed image files to upload
      console.log("Image files to upload:", imageFiles.length); // Debug log
      
      let imageUrls = [];
      if (imageFiles && imageFiles.length > 0) {
        imageUrls = await uploadImages();
        console.log("Uploaded image URLs:", imageUrls.length); // Debug log
      }
      
      const productToAdd = {
  ...newProduct,
  price: newProduct.isDeal ? parseFloat(newProduct.dealPrice) : parseFloat(newProduct.price),
  category: newProduct.isDeal ? 'Deals' : newProduct.category,
  subcategory: newProduct.subcategory || null,
  dealQuantity: newProduct.isDeal ? newProduct.dealQuantity : null,
  amount: parseInt(newProduct.amount, 10) || 0, // Ensure amount is a number
  addedOn: new Date(),
  initialAmount: parseInt(newProduct.amount, 10) || 0, // Ensure initialAmount is a number
  imageUrls: imageUrls ?? [], // Ensure imageUrls is an array
  hasBulkPricing: !!newProduct.hasBulkPricing, // Always true or false
  bulkOptions: newProduct.hasBulkPricing 
    ? (Array.isArray(newProduct.bulkOptions) ? 
        newProduct.bulkOptions
          .map(option => ({
            quantity: option.quantity || "", // Ensure quantity is a string
            price: option.price ? parseFloat(option.price) : null
          }))
          .filter(option => option.price !== null) // Remove invalid bulk options
        : []
      )
    : [], // If not bulk pricing, it should be an empty array
     hidden: false 
};
      
      await addDoc(collection(db, 'Products'), productToAdd);
      
      // Clear the form and image states
      setNewProduct({
  name: '',
  description: '',
  price: '',
  category: '',
  subcategory: '',
  details: '',
  imageUrls: [],
  isDeal: false,
  dealQuantity: '',
  dealPrice: '',
  hasBulkPricing: false,
  bulkOptions: [
    { quantity: 2, price: '' },
    { quantity: 3, price: '' },
    { quantity: 4, price: '' },
    { quantity: 5, price: '' },
    { quantity: 6, price: '' },
    { quantity: 7, price: '' },
    { quantity: 8, price: '' }
  ]
});
setImageFiles([]);
setSelectedImages([]);
      
      // Show success alert
      setAlertInfo({ 
        show: true, 
        message: `Product "${productToAdd.name}" added successfully!`, 
        type: 'success' 
      });
      
      // Hide alert after 5 seconds
      setTimeout(() => {
        setAlertInfo({ show: false, message: '', type: '' });
      }, 5000);
      
      fetchProducts();
    } catch (error) {
      console.error("Error adding product:", error);
      // Show error alert
      setAlertInfo({ 
        show: true, 
        message: `Error adding product: ${error.message}`, 
        type: 'error' 
      });
      
      // Hide alert after 5 seconds
      setTimeout(() => {
        setAlertInfo({ show: false, message: '', type: '' });
      }, 5000);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
  
    try {
      let updatedImageUrls = [...(editingProduct.imageUrls || [])];
      
      // Upload any new images
      if (imageFiles.length > 0) {
        const newUrls = await uploadImages();
        updatedImageUrls = [...updatedImageUrls, ...newUrls];
      }
  
      await updateProduct(editingProduct.id, {
        ...editingProduct,
        imageUrls: updatedImageUrls
      });
  
      setEditingProduct(null); // Close form
      setImageFiles([]);
    } catch (error) {
      console.error("Failed to update product:", error);
      setAlertInfo({ 
        show: true, 
        message: `Error updating product: ${error.message}`, 
        type: 'error' 
      });
    }
  };
  
  const updateProduct = async (id, updatedProduct) => {
    try {
      const productRef = doc(db, 'Products', id);
  
      const updatedData = {
        ...updatedProduct,
        category: isDeal ? 'Deals' : updatedProduct.category?.trim() || 'Uncategorized',
        price: updatedProduct.isDeal 
          ? parseFloat(updatedProduct.dealPrice || 0) 
          : parseFloat(updatedProduct.price || 0),
         isDeal: updatedProduct.isDeal || updatedProduct.category === 'Deals',
        amount: parseInt(updatedProduct.amount, 10),
        details: updatedProduct.details || '',
        hidden: typeof updatedProduct.hidden === 'boolean' ? updatedProduct.hidden : false,
        hasBulkPricing: updatedProduct.hasBulkPricing || false,
  bulkOptions: updatedProduct.hasBulkPricing
  ? (updatedProduct.bulkOptions || []).map(option => ({
      quantity: option.quantity,
      price: option.price !== '' && option.price !== undefined 
        ? parseFloat(option.price) 
        : null
    })).filter(option => option.price !== null)
  : []
      };
      
      // Remove temporary properties used for UI
      delete updatedData.imagePreviews;
      delete updatedData.image;
  
      await updateDoc(productRef, updatedData);
      console.log('Product updated successfully:', updatedData);
      fetchProducts(); // Refresh list
      setAlertInfo({ show: true, message: 'Product updated successfully!', type: 'success' });
    } catch (error) {
      console.error('Error updating product:', error);
      setAlertInfo({ show: true, message: `Update failed: ${error.message}`, type: 'error' });
    }
  };
  
  const deleteProduct = async (id) => {
    await deleteDoc(doc(db, 'Products', id));
    fetchProducts();
  };

  const toggleSidePanel = () => {
    setIsSidePanelOpen(!isSidePanelOpen);
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setIsSidePanelOpen(false);
  };

  const removeImageFromEditingProduct = (index) => {
    if (!editingProduct) return;
    
    const updatedImageUrls = [...editingProduct.imageUrls];
    updatedImageUrls.splice(index, 1);
    
    setEditingProduct({
      ...editingProduct,
      imageUrls: updatedImageUrls
    });
  };
  
  const filteredProducts = [...products].filter(product => {
  // First check if the product even exists
  if (!product) return false;
  
  // Then check category filtering
  if (filterSortOption.startsWith("category:")) {
    const selectedCategory = filterSortOption.split(":")[1].trim();
    
    // Check if category exists and handle case/whitespace differences
    return product.category && 
           product.category.trim().toLowerCase() === selectedCategory.trim().toLowerCase();
  }
  return true; // Include all products if not filtering by category
});

// SORT BY PRICE: Always sort products by price (low to high by default)
filteredProducts.sort((a, b) => {
  // Make sure prices are numbers for comparison
  const priceA = typeof a?.price === 'number' ? a.price : parseFloat(a?.price || 0);
  const priceB = typeof b?.price === 'number' ? b.price : parseFloat(b?.price || 0);
  
  // If we specifically want high to low (Z-A), then reverse the sort
  if (filterSortOption === "sort:desc") {
    return priceB - priceA; // High to low
  }
  
  // Default to low to high pricing
  return priceA - priceB; // Low to high
});

const groupedProducts = {};
if (filterSortOption === "sort:asc" || filterSortOption === "sort:desc") {
  filteredProducts.forEach(product => {
    if (!groupedProducts[product.category]) {
      groupedProducts[product.category] = [];
    }
    groupedProducts[product.category].push(product);
  });
}

  return (
    <div className="min-h-screen pb-4">
      <header className="fixed top-0 left-0 right-0 bg-white z-50" style={{
boxShadow: '0 2px 4px -1px rgba(37, 99, 235, 0.1), 0 1px 2px -1px rgba(16, 185, 129, 0.1)'
}}>
  <div className="container mx-auto px-3 py-1 h-16 sm:h-18 flex justify-between items-center">
    <AnimatedHamburger isOpen={isSidePanelOpen} toggle={toggleSidePanel} />

    {/* Wrapper for logo - more compact */}
    <div 
  className="flex flex-col items-center justify-center cursor-pointer"
  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
>
  <div className="flex items-center justify-center h-12 sm:h-14 w-24 sm:w-32">
    <img 
      src={logo} 
      alt="Logo" 
      className="max-h-full max-w-full object-contain" 
      style={{ transform: 'scale(3.5)' }}
    />
  </div>
</div>

    {/* Logout Button - more compact */}
    <button
      onClick={onLogout}
      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-sm rounded transition duration-200"
    >
      Logout
    </button>
  </div>
</header>

      <SidePanel 
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        categories={categories}
        onSelectCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        onLogout={onLogout}
        onOpenStoreLocator={() => handleViewChange('storeLocator')}
        isAdmin={true}
        onViewChange={handleViewChange}
        activeView={activeView}
      />

      <main className="container mx-auto px-4 mt-24 sm:mt-28">      
        {activeView === 'products' && (
          <>
            <h2 className="text-2xl font-bold mb-4">Manage Products - {selectedCategory}</h2>

            <div className="mb-4 flex gap-4">
              <button 
                onClick={() => {
                  setIsSelectMode(!isSelectMode);
                  setSelectedProducts([]);
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
              >
                {isSelectMode ? "Cancel Selection" : "Select"}
              </button>

              {isSelectMode && selectedProducts.length > 0 && (
                <button 
                  onClick={async () => {
                    for (const id of selectedProducts) {
                      await deleteProduct(id);
                    }
                    setSelectedProducts([]);
                    setIsSelectMode(false);
                  }}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
                >
                  Delete Selected ({selectedProducts.length})
                </button>
              )}
            </div>
            
            <div className="mb-4">
              <select
                value={filterSortOption}
                onChange={(e) => setFilterSortOption(e.target.value)}
                className="border rounded p-2"
              >
                <option value="sort:asc">Sort: Price Low to High</option>
                <option value="sort:desc">Sort: Price High to Low</option>
                <option disabled>──────────────</option>
                {Array.from(new Set(products.map(p => p.category))).map(category => (
                  <option key={category} value={`category:${category}`}>
                    Category: {category}
                  </option>
                ))}
              </select>
            </div>

            {editingProduct && (
  <div className="edit-product-form bg-white p-6 rounded-lg shadow-lg mb-8">
    <h3 className="text-xl font-bold mb-4">Edit Product</h3>
    <form onSubmit={handleEditSubmit}>
      {/* Conditional Form for Deals */}
      {editingProduct.isDeal ? (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Items (One per line)</label>
            <textarea 
              value={Array.isArray(editingProduct.name) ? editingProduct.name.join("\n") : editingProduct.name}
              onChange={(e) => setEditingProduct({
                ...editingProduct,
                name: e.target.value.split("\n")
              })}
              rows="4"
              className="border rounded p-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Quantity</label>
            <input 
              type="text"
              value={editingProduct.dealQuantity || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, dealQuantity: e.target.value })}
              className="border rounded p-2 w-full"
              placeholder="Enter deal quantity (e.g., 3 for $20)"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Price ($)</label>
            <input 
              type="number"
              step="0.01"
              value={editingProduct.dealPrice || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, dealPrice: e.target.value })}
              className="border rounded p-2 w-full"
              placeholder="Enter deal price"
            />
          </div>
        </>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input 
              type="text" 
              value={editingProduct.name} 
              onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
              className="border rounded p-2 w-full"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
            <input 
              type="number" 
              value={editingProduct.price} 
              onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
              className="border rounded p-2 w-full"
            />
          </div>
        </>
      )}

      {/* Description - Always Visible */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea 
          value={editingProduct.description} 
          onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
          className="border rounded p-2 w-full"
          rows="3"
        />
      </div>

      <div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
  <select
  value={editingProduct.category || ''}

  onChange={(e) => {
    const newCategory = e.target.value;

    setEditingProduct(prev => {
  const isNowDeal = newCategory === 'Deals';
  return {
    ...prev,
    category: newCategory,
    isDeal: isNowDeal,
    price: isNowDeal ? parseFloat(prev.dealPrice || 0) : parseFloat(prev.price || 0)
  };
});

  }}

  className="border rounded p-2 w-full"
>
  <option value="" disabled>Select category</option>
  {existingCategories.map((cat, index) => (
    <option key={index} value={cat}>{cat}</option>
  ))}
</select>

</div>
<div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
  <select
    value={editingProduct.subcategory || ''}
    onChange={(e) => setEditingProduct({ ...editingProduct, subcategory: e.target.value })}
    className="border rounded p-2 w-full"
  >
    <option value="" disabled>Select subcategory</option>
    {existingSubcategories.map((subcat, index) => (
      <option key={index} value={subcat}>{subcat}</option>
    ))}
  </select>
</div>

      {/* Current Images Section */}
<div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">Current Images</label>
  {editingProduct.imageUrls && editingProduct.imageUrls.length > 0 ? (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {editingProduct.imageUrls.map((url, index) => (
        <div
          key={index}
          className="relative transition-transform duration-200 ease-in-out transform"
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e)}
          onDrop={(e) => handleDrop(e, index)}
          style={{
            transition: "transform 0.2s ease-in-out",
          }}
        >
          <img 
            src={url} 
            alt={`Product ${index + 1}`} 
            className="h-24 w-24 object-cover rounded" 
          />
          <button 
            type="button"
            onClick={() => removeImageFromEditingProduct(index)}
            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
          >
            ❌
          </button>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-gray-500">No images uploaded</p>
  )}
</div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Add New Images</label>
        <input 
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageChange}
          className="border rounded p-2 w-full"
        />
      </div>

      {/* Bulk Pricing Toggle */}
<div className="mb-6 flex justify-center">
  <label
    htmlFor="editHasBulkPricing"
    className="flex items-center gap-3 cursor-pointer select-none p-4  rounded-lg hover:bg-green-100 focus-within:ring-2 focus-within:ring-green-500 shadow-sm"
  >
    <input
      type="checkbox"
      id="editHasBulkPricing"
      checked={editingProduct.hasBulkPricing || false}
      onChange={(e) => {
        e.stopPropagation();
        if (e.target.checked) {
          setEditingProduct({
            ...editingProduct,
            hasBulkPricing: true,
            bulkOptions: [
              { quantity: 2, price: editingProduct.bulkOptions?.[0]?.price || '' },
              { quantity: 3, price: editingProduct.bulkOptions?.[1]?.price || '' },
              { quantity: 4, price: editingProduct.bulkOptions?.[2]?.price || '' },
              { quantity: 5, price: editingProduct.bulkOptions?.[3]?.price || '' },
              { quantity: 6, price: editingProduct.bulkOptions?.[4]?.price || '' },
              { quantity: 7, price: editingProduct.bulkOptions?.[5]?.price || '' },
              { quantity: 8, price: editingProduct.bulkOptions?.[6]?.price || '' }
            ]
          });
        } else {
          setEditingProduct({
            ...editingProduct,
            hasBulkPricing: false,
            bulkOptions: []
          });
        }
      }}
      className="w-6 h-6"
    />
    <span className="text-base font-semibold text-gray-700">Enable Bulk Pricing Options</span>
  </label>
</div>



     {/* Bulk Pricing Fields */}
{editingProduct.hasBulkPricing && editingProduct.bulkOptions && (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Pricing Options</label>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {editingProduct.bulkOptions.map((option, index) => (
        <div key={index} className="border rounded p-2">
          <label className="text-xs font-medium">{option.quantity}x Price</label>
          <input 
            type="number"
            value={option.price || ''}
            onChange={(e) => {
              const updatedOptions = [...editingProduct.bulkOptions];
              updatedOptions[index].price = e.target.value;
              setEditingProduct({ 
                ...editingProduct, 
                bulkOptions: updatedOptions 
              });
            }}
            className="border rounded p-1 w-full"
          />
        </div>
      ))}
    </div>
  </div>
)}

      {/* Save and Cancel Buttons */}
      <div className="flex gap-2 mt-4">
        <button 
          type="submit" 
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Save Changes
        </button>
        <button 
          type="button"
          onClick={() => setEditingProduct(null)} 
          className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </form>
  </div>
)}



            {(filterSortOption === "sort:asc" || filterSortOption === "sort:desc") ? (
  // Grouped view by category and subcategory
Object.keys(groupedProducts).sort().map(category => {
  // Create a map of subcategories within each category
  const subcatMap = {};
  groupedProducts[category].forEach(product => {
    const sub = product.subcategory?.trim() || 'Uncategorized';
    if (!subcatMap[sub]) subcatMap[sub] = [];
    subcatMap[sub].push(product);
  });

  return (
    <div key={category} className="mb-6">
      <h3 className="text-xl font-semibold mb-3 pb-2 border-b-2 border-gray-200">
        {category}
      </h3>

      {Object.entries(subcatMap).sort().map(([subcat, productsInSubcat]) => (
        <div key={subcat} className="mb-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2">{subcat}</h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-3">
            {productsInSubcat.map(product => (
              <li 
                key={product.id} 
                className={`mb-2 p-3 border rounded relative bg-white shadow-md flex flex-col space-y-2 max-w-xs ${isSelectMode ? 'pl-10' : ''}`}
              >
                {/* [ ... all your existing content inside <li> ... ] */}

                {/* Product Title */}
                <h3 className="font-bold text-lg whitespace-pre-line">
                  {Array.isArray(product.name)
                    ? product.name.join("\n") 
                    : product.name.includes(",") 
                      ? product.name.split(",").join("\n")
                      : product.name}
                </h3>

                {/* Image Display */}
                <div className="flex gap-2 mt-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                  {product.imageUrls && product.imageUrls.length > 0 ? (
                    product.imageUrls.map((url, index) => (
                      <img 
                        key={index} 
                        src={url} 
                        alt={`${product.name} ${index + 1}`} 
                        className="h-20 w-20 object-cover rounded border"
                      />
                    ))
                  ) : (
                    <div className="w-20 h-20 flex items-center justify-center bg-gray-100 text-gray-500 text-xs">
                      No image available
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="mt-2 text-sm text-gray-700 whitespace-pre-line">
                  {expandedProduct === product.id ? (
                    <p>{product.description || "No description available."}</p>
                  ) : (
                    <p className="line-clamp-6">
                      {Array.isArray(product.description)
                        ? product.description.join("\n")
                        : product.description?.includes("\n")
                          ? product.description
                          : product.description?.length > 200 
                            ? `${product.description.slice(0, 200)}...` 
                            : product.description || "No description available."}
                    </p>
                  )}
                </div>

                {/* Read More */}
                <div className="flex justify-end mt-2">
                  <button 
                    onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition"
                  >
                    {expandedProduct === product.id ? "Show Less" : "Read More"}
                  </button>
                </div>

                {/* Price & Bulk */}
                <div className="mt-1 text-green-600 font-semibold text-sm">
                  <p>

                    {product.category === 'Deals'
                      ? product.dealQuantity
                        ? `${product.dealQuantity} - $${parseFloat(product.price).toFixed(2)}`
                        : `$${parseFloat(product.price).toFixed(2)}`
                      : `Price: $${parseFloat(product.price).toFixed(2)}`}

                  </p>
                  {product.hasBulkPricing && product.bulkOptions?.length > 0 && (
                    <div className="mt-1 flex flex-col space-y-1">
                      <p className="font-semibold text-xs text-gray-600">Bulk Prices:</p>
                      {product.bulkOptions.map((option, index) => (
                        <div key={index} className="flex justify-between text-xs text-gray-600">
                          <span>{option.quantity}x</span>
                          <span>${parseFloat(option.price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Category/Subcategory Info */}
                <p className="mt-1 text-xs text-gray-500">
                  Category: {product.category}
                  {product.subcategory && (
                    <span className="ml-2">| Subcategory: <span className="text-gray-700">{product.subcategory}</span></span>
                  )}
                </p>

                {/* Details */}
                {expandedProduct === product.id && product.details && (
                  <div className="mt-2">
                    <p className="font-semibold">Additional Details:</p>
                    <p className="text-sm mt-1 whitespace-pre-line">{product.details}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-2">
                  <button 
  onClick={() => {
    setEditingProduct(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }} 
  className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition"
>
  Edit
</button>
                  <button
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this product?")) {
                        deleteProduct(product.id);
                      }
                    }}
                    className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition"
                  >
                    Delete
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await updateProduct(product.id, { ...product, hidden: !product.hidden });
                        await fetchProducts();
                      } catch (err) {
                        console.error('Error toggling visibility:', err);
                      }
                    }}
                    className={`${
                      product.hidden ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-300 hover:bg-gray-400'
                    } text-white px-3 py-1 rounded text-xs transition`}
                  >
                    {product.hidden ? 'Unhide' : 'Hide'}
                  </button>

                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
})

) : (
  // Flat list when filtering by category
<ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-3">
  {filteredProducts.map(product => (
    <li 
      key={product.id} 
      className={`mb-2 p-3 border rounded relative bg-white shadow-md flex flex-col space-y-2 max-w-xs ${isSelectMode ? 'pl-10' : ''}`}
    >
      {isSelectMode && (
        <input 
          type="checkbox"
          checked={selectedProducts.includes(product.id)}
          onChange={(e) => {
            const newSelection = e.target.checked
              ? [...selectedProducts, product.id]
              : selectedProducts.filter(id => id !== product.id);
            setSelectedProducts(newSelection);
          }}
          className="absolute top-2 left-2"
        />
      )}

      <h3 className="font-bold text-lg whitespace-pre-line">
        {Array.isArray(product.name)
          ? product.name.join("\n") 
          : product.name.includes(",") 
            ? product.name.split(",").join("\n")
            : product.name}
      </h3>

      <div className="flex gap-2 mt-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
        {product.imageUrls && product.imageUrls.length > 0 ? (
          product.imageUrls.map((url, index) => (
            <img 
              key={index} 
              src={url} 
              alt={`${product.name} ${index + 1}`} 
              className="h-20 w-20 object-cover rounded border"
            />
          ))
        ) : (
          <div className="w-20 h-20 flex items-center justify-center bg-gray-100 text-gray-500 text-xs">
            No image available
          </div>
        )}
      </div>

      <div className="mt-2 text-sm text-gray-700 whitespace-pre-line">
        {expandedProduct === product.id ? (
          <p>{product.description || "No description available."}</p>
        ) : (
          <p className="line-clamp-6">
            {product.description?.length > 200 
              ? `${product.description.slice(0, 200)}...` 
              : product.description || "No description available."}
          </p>
        )}
      </div>

      <div className="flex justify-end mt-2">
        <button 
          onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
          className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition"
        >
          {expandedProduct === product.id ? "Show Less" : "Read More"}
        </button>
      </div>

      <div className="mt-1 text-green-600 font-semibold text-sm">
        <p>
          {product.category === 'Deals' && product.dealQuantity 
            ? `${product.dealQuantity} - $${parseFloat(product.price).toFixed(2)}` 
            : `Price: $${parseFloat(product.price).toFixed(2)}`}
        </p>

        {product.hasBulkPricing && product.bulkOptions?.length > 0 && (
          <div className="mt-1 flex flex-col space-y-1">
            <p className="font-semibold text-xs text-gray-600">Bulk Prices:</p>
            {product.bulkOptions.map((option, index) => (
              <div key={index} className="flex justify-between text-xs text-gray-600">
                <span>{option.quantity}x</span>
                <span>${parseFloat(option.price).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="mt-1 text-xs text-gray-500">
        Category: {product.category}
        {product.subcategory && (
          <span className="ml-2">| Subcategory: <span className="text-gray-700">{product.subcategory}</span></span>
        )}
      </p>

      {expandedProduct === product.id && (
        <div className="mt-2">
          <p>Added on: {formatDate(product.addedOn)}</p>
          {product.details && (
            <div className="mt-2">
              <p className="font-semibold">Additional Details:</p>
              <p className="text-sm mt-1 whitespace-pre-line">{product.details}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-2">
        <button 
  onClick={() => {
    setEditingProduct(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }} 
  className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition"
>
  Edit
</button>
        <button
          onClick={() => {
            if (window.confirm("Are you sure you want to delete this product?")) {
              deleteProduct(product.id);
            }
          }}
          className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition"
        >
          Delete
        </button>
        <button
          onClick={async () => {
            try {
              await updateProduct(product.id, { ...product, hidden: !product.hidden });
            } catch (err) {
              console.error('Error toggling visibility:', err);
            }
          }}
          className={`${
            product.hidden ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-300 hover:bg-gray-400'
          } text-white px-3 py-1 rounded text-xs transition`}
        >
          {product.hidden ? 'Unhide' : 'Hide'}
        </button>
      </div>
    </li>
  ))}
</ul>





/*ADDING A PRODUCT*/

)}
          </>
        )}  
      

        {activeView === 'addProduct' && (
          <>
            <h2 className="text-2xl font-bold mb-4">Add New Product</h2>
            
            {/* Alert Notification */}
            {alertInfo.show && (
              <div className={`mb-4 p-4 rounded-lg ${
                alertInfo.type === 'success' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' : 
                'bg-red-50 text-red-800 border-l-4 border-red-500'
              } flex items-center`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  {alertInfo.type === 'success' ? (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  )}
                </svg>
                <span>{alertInfo.message}</span>
              </div>
            )}
            
            <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Add as Deal
  </label>
  <input
    type="checkbox"
    checked={newProduct.isDeal}
    onChange={() => setNewProduct({ 
      ...newProduct, 
      isDeal: !newProduct.isDeal, 
      category: 'Deals', 
      price: '',  // Clear the standard price field
      dealQuantity: '', 
      dealPrice: '' 
    })}
    className="mr-2"
  />
</div>    

            <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
              <form onSubmit={addProduct} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div className="space-y-4">
                    {/* Product Name */}
                    <div>
                      
                      {/* Product Name / Deal Items */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    {newProduct.isDeal ? "Deal Items (One per line)" : "Product Name"}
  </label>

  {newProduct.isDeal ? (
    <textarea
      placeholder="Enter each item name on a new line"
      value={Array.isArray(newProduct.name) ? newProduct.name.join("\n") : newProduct.name}
      onChange={(e) => setNewProduct({
        ...newProduct,
        name: e.target.value.split("\n") // Store each line as an array
      })}
      rows="4"
      className="border rounded p-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
      required
    />
  ) : (
    <input
      type="text"
      placeholder="Enter product name"
      value={newProduct.name}
      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
      className="border rounded p-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
      required
    />
  )}
</div>

                    </div>
                    
                    {/* Conditionally show Deal Fields */}
{newProduct.isDeal ? (
  <>
    <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Deal Quantity (e.g., 3 for $20, Buy 2 Get 1 Free)
  </label>
  <input
    type="text" // Changed from "number" to "text"
    value={newProduct.dealQuantity}
    onChange={(e) => setNewProduct({ ...newProduct, dealQuantity: e.target.value })}
    className="border rounded p-2 w-full"
    placeholder="Enter deal quantity description"
    required
  />
</div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Deal Price ($)
      </label>
      <input
        type="number"
        step="0.01"
        value={newProduct.dealPrice}
        onChange={(e) => setNewProduct({ ...newProduct, dealPrice: e.target.value })}
        className="border rounded p-2 w-full"
        required
      />
    </div>
  </>
) : (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Price ($)
    </label>
    <input
      type="number"
      step="0.01"
      value={newProduct.price}
      onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
      className="border rounded p-2 w-full"
      required
    />
  </div>
)}

                    
              {/* Bulk Pricing Section - Only shown if not a deal */}
{!newProduct.isDeal && (
  <div className="col-span-full mb-6 flex justify-center">
    <label
      htmlFor="hasBulkPricing"
      className="flex items-center gap-3 cursor-pointer select-none p-4  rounded-lg hover:bg-green-100 focus-within:ring-2 focus-within:ring-green-500 shadow-sm"
    >
      <input
        type="checkbox"
        id="hasBulkPricing"
        checked={newProduct.hasBulkPricing}
        onChange={(e) => {
          e.stopPropagation();
          if (e.target.checked) {
            setNewProduct({
              ...newProduct,
              hasBulkPricing: true,
              bulkOptions: [
                { quantity: 2, price: newProduct.bulkOptions?.[0]?.price || '' },
                { quantity: 3, price: newProduct.bulkOptions?.[1]?.price || '' },
                { quantity: 4, price: newProduct.bulkOptions?.[2]?.price || '' },
                { quantity: 5, price: newProduct.bulkOptions?.[3]?.price || '' },
                { quantity: 6, price: newProduct.bulkOptions?.[4]?.price || '' },
                { quantity: 7, price: newProduct.bulkOptions?.[5]?.price || '' },
                { quantity: 8, price: newProduct.bulkOptions?.[6]?.price || '' }
              ]
            });
          } else {
            setNewProduct({
              ...newProduct,
              hasBulkPricing: false,
              bulkOptions: []
            });
          }
        }}
        className="w-6 h-6"
      />
      <span className="text-base font-semibold text-gray-700">Enable Bulk Pricing Options</span>
    </label>
  </div>
)}

{/* Bulk Pricing Options - Shown if enabled */}
{!newProduct.isDeal && newProduct.hasBulkPricing && (
  <div className="col-span-full mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Bulk Pricing Options
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {newProduct.bulkOptions.map((option, index) => (
        <div key={index} className="border rounded p-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {option.quantity}x Price
          </label>
          <input
            type="number"
            step="0.01"
            placeholder={`Price for ${option.quantity} items`}
            value={option.price}
            onChange={(e) => {
              const updatedBulkOptions = [...newProduct.bulkOptions];
              updatedBulkOptions[index].price = e.target.value;
              setNewProduct({ ...newProduct, bulkOptions: updatedBulkOptions });
            }}
            className="border rounded p-1 w-full text-sm"
          />
        </div>
      ))}
    </div>
    <p className="text-xs text-gray-500 mt-1">
      Enter the total price for each bulk quantity (leave blank if not applicable)
    </p>
  </div>
)}


                    
                    {/* Category Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <CategoryCombobox
                        categories={categories.filter(c => c !== 'All')}
                        value={newProduct.category}
                        onChange={(value) => setNewProduct({...newProduct, category: value})}
                      />
                    </div>

                  <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                  <input 
                  type="text"
                  list="subcategoryOptions"
                  value={newProduct.subcategory || ''}
                  onChange={(e) => setNewProduct({ ...newProduct, subcategory: e.target.value })}
                  className="border rounded p-2 w-full"
                  placeholder="Enter or select subcategory"
                  />
                  <datalist id="subcategoryOptions">
                  {existingSubcategories.map((subcat, index) => (
                  <option key={index} value={subcat} />
                  ))}
                  </datalist>
                  </div>
                  </div>
                  
                  {/* Right column */}
                  <div className="space-y-4">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        placeholder="Enter product description"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        className="border rounded p-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        rows="4"
                        
                      />
                    </div>
                    
                    {/* Additional Details */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Details <span className="text-xs text-gray-500">(optional)</span>
                      </label>
                      <textarea
                        placeholder="Enter additional product details"
                        value={newProduct.details}
                        onChange={(e) => setNewProduct({...newProduct, details: e.target.value})}
                        className="border rounded p-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        rows="4"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Product Images Section */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
                  <div className="border-dashed border-2 border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="space-y-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-gray-600">Click to upload product images</p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    </label>
                  </div>
                  
                  {/* Show image previews */}
                  {selectedImages.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Images</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {selectedImages.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`} 
                              className="h-24 w-full object-cover rounded-md border border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
                              <button 
                                type="button" 
                                className="p-1 bg-red-500 rounded-full text-white"
                                onClick={() => {
                                  // Remove image at this index
                                  const newImages = [...selectedImages];
                                  newImages.splice(index, 1);
                                  setSelectedImages(newImages);
                                  
                                  const newFiles = [...imageFiles];
                                  newFiles.splice(index, 1);
                                  setImageFiles(newFiles);
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
  
                {/* Submit Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button 
                    type="submit" 
                    className="w-full bg-green-600 text-white p-3 rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-300"
                  >
                    Add Product
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {activeView === 'storeLocator' && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Store Locator</h2>
            <StoreLocator isAdmin={true} />
          </div>
        )}

        {activeView === 'announcement' && !editingAnnouncement && (
  <AnnouncementForm 
    mode="create"
    onBack={() => setActiveView('products')} 
  />
)}

{activeView === 'manageAnnouncements' && (
  <ManageAnnouncements 
    onBack={() => setActiveView('products')} 
    onEdit={(announcement) => {
      setEditingAnnouncement(announcement);
      setActiveView('editAnnouncement');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }}
  />
)}

{activeView === 'editAnnouncement' && editingAnnouncement && (
  <AnnouncementForm 
    mode="edit"
    existingData={editingAnnouncement} 
    onSubmit={() => {
      setActiveView('manageAnnouncements');
      setEditingAnnouncement(null);
    }} 
  />
)}



        {activeView === 'orders' && (
          <div className="mb-8">
            <AdminOrderManagement />
          </div>
        )}

        {activeView === 'messages' && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-lg">
              <Messages 
                isAdmin={true} 
                isWorker={false}
                key="admin-messages"
              />
            </div>
          </div>
        )}
      </main>
      {showCropper && (
        <CropImageModal 
          imageSrc={croppingImage}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropper(false);
            setCroppingImage(null);
            // Reset image files if user cancels during the first image crop
            if (currentCropIndex === 0) {
              setImageFiles([]);
              setSelectedImages([]);
              if (editingProduct) {
                setEditingProduct({...editingProduct, imagePreviews: []});
              }
            }
          }}
          onClose={() => setShowCropper(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;